import React, { useState, useEffect } from "react";
import {
    Box,
    Grid,
    Typography,
    Checkbox,
    FormGroup,
    FormControlLabel,
    Slider,
    Select,
    MenuItem,
    Button,
    Container,
    TextField,
    Paper,
    useTheme,
    alpha,
} from "@mui/material";
import ProjectCard from "../components/ProjectCard";
import { ApiHelper } from "../utils/apiHelper";

export default function ProjectFilterPage() {
    const theme = useTheme();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [fundingRange, setFundingRange] = useState([0, 200]);
    const [sortOrder, setSortOrder] = useState("popular"); 
    const [sector, setSector] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    const handleFundingChange = (event, newValue) => {
        setFundingRange(newValue);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                setLoading(true);
                const response = await ApiHelper.get("/startup/active");
                if (response.data.code === "00000") {
                    const formattedProjects = response.data.startups.map(item => ({
                        id: item.startup.startupId,
                        startupId: item.startup.startupId,
                        logoSrc: item.profile.mainLogoPath || "/img/default-logo.png",
                        projectSrc: "/img/project.jpeg",
                        title: item.profile.name || "İsimsiz Kampanya",
                        description: item.profile.shortDescription || "Açıklama bulunmuyor",
                        fundedAmount: parseFloat(item.funding.details?.fundingNeeded || 0),
                        targetAmount: parseFloat(item.funding.details?.valuation || 0),
                        investors: 0, // API'den gelmiyorsa 0 olarak bırakıyoruz
                        highestInvestment: 0, // API'den gelmiyorsa 0 olarak bırakıyoruz
                        campaignEndDate: "Belirlenmedi", // API'den tarih gelirse burası güncellenebilir
                        category: item.profile.mainCategories?.[0] || "Diğer",
                        sector: item.profile.sectors?.[0] || "Diğer",
                        status: item.startup.status
                    }));
                    setProjects(formattedProjects);
                }
            } catch (error) {
                setError("Projeler yüklenirken bir hata oluştu");
                console.error("Error fetching projects:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchProjects();
    }, []);

    const filteredProjects = projects.filter(project => {
        // Kategori filtresi
        if (selectedCategories.length > 0 && !selectedCategories.includes(project.category)) {
            return false;
        }

        // Fonlama aralığı filtresi
        const projectFunding = project.fundedAmount;
        if (projectFunding < fundingRange[0] * 1000 || projectFunding > fundingRange[1] * 1000) {
            return false;
        }

        // Arama filtresi
        if (searchTerm && !project.title.toLowerCase().includes(searchTerm.toLowerCase())) {
            return false;
        }

        return true;
    });

    const sortedProjects = [...filteredProjects].sort((a, b) => {
        switch (sortOrder) {
            case "popular":
                return b.investors - a.investors;
            case "new":
                return new Date(b.registerTime) - new Date(a.registerTime);
            case "funded":
                return b.fundedAmount - a.fundedAmount;
            default:
                return 0;
        }
    });

    return (
        <Container maxWidth="lg">
            <Box sx={{ display: "flex", gap: 4, py: 4, flexDirection: { xs: "column", md: "row" } }}>
                <Paper
                    elevation={2}
                    sx={{
                        width: { xs: "100%", md: "30%" },
                        bgcolor: theme.palette.background.paper,
                        p: 3,
                        borderRadius: 2,
                        mb: { xs: 4, md: 0 },
                    }}
                >
                    <Typography variant="h6" color="primary" gutterBottom>
                        Filtreleme
                    </Typography>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" color="textPrimary" gutterBottom>
                            Arama
                        </Typography>
                        <TextField
                            placeholder="Proje adı veya anahtar kelime..."
                            variant="outlined"
                            fullWidth
                            value={searchTerm}
                            onChange={handleSearchChange}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': {
                                        borderColor: theme.palette.primary.main,
                                    },
                                    '& fieldset': {
                                        borderColor: alpha(theme.palette.primary.main, 0.2),
                                    },
                                },
                            }}
                        />
                    </Box>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" color="textPrimary" gutterBottom>
                            Sektör
                        </Typography>
                        <Select
                            value={sector}
                            onChange={(e) => setSector(e.target.value)}
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: alpha(theme.palette.primary.main, 0.2),
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.primary.main,
                                },
                            }}
                        >
                            <MenuItem value="">Tüm Sektörler</MenuItem>
                            <MenuItem value="Technology">Teknoloji</MenuItem>
                            <MenuItem value="Finance">Finans</MenuItem>
                            <MenuItem value="Healthcare">Sağlık</MenuItem>
                        </Select>
                    </Box>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" color="textPrimary" gutterBottom>
                            Kategoriler
                        </Typography>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        sx={{
                                            color: alpha(theme.palette.primary.main, 0.7),
                                            '&.Mui-checked': {
                                                color: theme.palette.primary.main,
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography color="textPrimary">
                                        Startup
                                    </Typography>
                                }
                                onChange={(e) =>
                                    setSelectedCategories((prev) =>
                                        e.target.checked
                                            ? [...prev, "Startup"]
                                            : prev.filter((cat) => cat !== "Startup")
                                    )
                                }
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        sx={{
                                            color: alpha(theme.palette.primary.main, 0.7),
                                            '&.Mui-checked': {
                                                color: theme.palette.primary.main,
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography color="textPrimary">
                                        Blockchain
                                    </Typography>
                                }
                                onChange={(e) =>
                                    setSelectedCategories((prev) =>
                                        e.target.checked
                                            ? [...prev, "Blockchain"]
                                            : prev.filter((cat) => cat !== "Blockchain")
                                    )
                                }
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        sx={{
                                            color: alpha(theme.palette.primary.main, 0.7),
                                            '&.Mui-checked': {
                                                color: theme.palette.primary.main,
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography color="textPrimary">
                                        AI/ML
                                    </Typography>
                                }
                                onChange={(e) =>
                                    setSelectedCategories((prev) =>
                                        e.target.checked
                                            ? [...prev, "AI/ML"]
                                            : prev.filter((cat) => cat !== "AI/ML")
                                    )
                                }
                            />
                        </FormGroup>
                    </Box>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" color="textPrimary" gutterBottom>
                            Fonlama Aralığı (Bin TRY)
                        </Typography>
                        <Slider
                            value={fundingRange}
                            onChange={handleFundingChange}
                            valueLabelDisplay="auto"
                            min={0}
                            max={200}
                            sx={{
                                color: theme.palette.primary.main,
                                '& .MuiSlider-thumb': {
                                    '&:hover, &.Mui-focusVisible': {
                                        boxShadow: `0px 0px 0px 8px ${alpha(theme.palette.primary.main, 0.16)}`,
                                    },
                                },
                                '& .MuiSlider-rail': {
                                    opacity: 0.32,
                                },
                            }}
                        />
                        <Typography variant="caption" color="textSecondary">
                            {fundingRange[0]}K - {fundingRange[1]}K TRY
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="subtitle1" color="textPrimary" gutterBottom>
                            Sırala
                        </Typography>
                        <Select
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: alpha(theme.palette.primary.main, 0.2),
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.primary.main,
                                },
                            }}
                        >
                            <MenuItem value="popular">En Popüler</MenuItem>
                            <MenuItem value="new">En Yeni</MenuItem>
                            <MenuItem value="funded">En Çok Fonlanan</MenuItem>
                        </Select>
                    </Box>

                    <Button
                        variant="contained"
                        fullWidth
                        sx={{
                            mt: 4,
                            bgcolor: theme.palette.primary.main,
                            '&:hover': {
                                bgcolor: theme.palette.primary.dark,
                            },
                        }}
                    >
                        Filtrele
                    </Button>
                </Paper>

                <Box sx={{ flexGrow: 1 }}>
                    <Grid container spacing={4}>
                        {sortedProjects.map((project) => (
                            <Grid item xs={12} sm={6} md={6} key={project.id}>
                                <ProjectCard {...project} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Box>
        </Container>
    );
}
